import { IconButton, type DataTableColumn, type TableRow } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import type { MetricWindow } from "@/api";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { ObservabilityStateBlock } from "./ObservabilityStateBlock";
import { useObservabilityMetric } from "./useObservabilityMetric";
import styles from "./ObservabilityPage.module.css";

/**
 * 표를 그리는 탭들(테이블·쿼리·지연)이 공유하는 셸이다.
 *
 * 세 응답 모두 서버가 **고정 정렬로 상한만큼** 골라서 보낸다. 계약에 정렬·필터·페이지네이션
 * 파라미터가 없다. 그래서 표 위 띠에 결과 수(`FilterResultCount`)를 표시하지 않는다. "20건" 은
 * 이 구간에 20건이 있었다는 뜻이 아니라 서버가 20건에서 잘랐다는 뜻이다. 결과 수로 표시하면
 * 사용자가 모집단을 잘못 읽는다. 대신 캡션이 정렬 기준과 상위 몇 건인지를 표시한다.
 *
 * 행 순서는 `toRows` 가 정한다. 각 탭이 다음 중 하나를 고른다:
 * - 서버 정렬을 그대로 쓴다(hotspot 두 탭).
 * - 다시 정렬한다(지연 탭). 서버는 호출 수 순서로 주지만, 화면은 느린 행부터 보여야 한다.
 */
export function ObservabilityTablePanel<Item, Row extends TableRow>({
  window,
  fetcher,
  toRows,
  columns,
  note,
  loadingText,
  emptyMessage,
  ariaLabel,
  minWidth,
}: {
  window: MetricWindow;
  fetcher: (window: MetricWindow) => Promise<Item[]>;
  toRows: (items: readonly Item[]) => Row[];
  columns: readonly DataTableColumn<Row>[];
  /** 고정 정렬과 상한을 설명하는 한 줄이다. 조회 조건이 아니라 사실 설명이라 필터 칩이 아니다. */
  note: string;
  loadingText: string;
  emptyMessage: string;
  ariaLabel: string;
  minWidth: string;
}): ReactNode {
  const { state, reload } = useObservabilityMetric(fetcher, window);
  const isLoading = state.status === "loading" || state.status === "idle";
  return (
    <ObservabilityStateBlock state={state} loadingText={loadingText} onRetry={reload}>
      {(items) => (
        <PanelTable
          items={items}
          toRows={toRows}
          columns={columns}
          note={note}
          emptyMessage={emptyMessage}
          ariaLabel={ariaLabel}
          minWidth={minWidth}
          isLoading={isLoading}
          onReload={reload}
        />
      )}
    </ObservabilityStateBlock>
  );
}

function PanelTable<Item, Row extends TableRow>({
  items,
  toRows,
  columns,
  note,
  emptyMessage,
  ariaLabel,
  minWidth,
  isLoading,
  onReload,
}: {
  items: readonly Item[];
  toRows: (items: readonly Item[]) => Row[];
  columns: readonly DataTableColumn<Row>[];
  note: string;
  emptyMessage: string;
  ariaLabel: string;
  minWidth: string;
  isLoading: boolean;
  onReload: () => void;
}): ReactNode {
  // 응답이 이미 상한만큼 잘려 와서 페이징이 없다. 데이터가 바뀌면 소스를 새로 만든다.
  const source = useMemo(() => new StaticTableSource(toRows(items)), [items, toRows]);
  return (
    <div className={styles.tablePanel}>
      <div className={bulkActionBarStyles.host}>
        <BulkActionBar
          source={source}
          idleContent={<span className={styles.tableNote}>{note}</span>}
          rightContent={
            <IconButton
              text={isLoading ? "로딩 중…" : "새로고침"}
              size="sm"
              variant="surface"
              disabled={isLoading}
              onClick={onReload}
            />
          }
          ariaLabel={`${ariaLabel} 도구`}
        />
        <HatDataTable
          source={source}
          columns={columns}
          emptyMessage={emptyMessage}
          loadingMessage="불러오고 있습니다."
          ariaLabel={ariaLabel}
          scrollable
          minWidth={minWidth}
        />
      </div>
    </div>
  );
}
